import { useEffect, useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import * as LucideIcons from 'lucide-react'
import { CalendarClock, MapPin, Beer, Radio, ArrowRight, Clock, Trophy, AlertTriangle } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import PageHeader from '@/components/PageHeader'
import { programmeAPI } from '@/lib/api'
import { useProgrammeSocket } from '@/lib/programmeSocket'
import { useAuth } from '@/hooks/useAuth'
import { can } from '@/lib/permissions'
import SubNav from '@/components/SubNav'

/* Track-identiteit: elk spoor krijgt z'n eigen kleur + icoon, zodat je in één
   oogopslag ziet of iets in de zaal, aan de bar of op het podium gebeurt. */
const TRACKS = {
  zaal:    { label: 'Zaal',                    icon: MapPin,        color: '#7BC8FF' },
  bar:     { label: 'Bar',                     icon: Beer,          color: '#FFB57A' },
  show:    { label: 'Show',                    icon: Radio,         color: '#B4FF39' },
  compo:   { label: 'Compo',                   icon: Trophy,        color: '#C77BFF' },
  kritiek: { label: 'Kritieke aankondigingen', icon: AlertTriangle, color: '#FF5A5A' },
}

const fmtTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })
}
const fmtDay = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' })
}

export default function ProgrammePage() {
  const { user } = useAuth()
  const isAdmin = can(user?.role, 'manage_users')

  const [slots, setSlots]     = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [nowTs, setNowTs]     = useState(() => Date.now())

  const load = useCallback(() => {
    programmeAPI.list()
      .then(r => setSlots(r.data?.slots ?? []))
      .catch(e => setError(e.response?.data?.error ?? e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])
  useProgrammeSocket(load)

  // Elke 30s de "nu"-marker verschuiven zodat de tijdlijn meeleeft zonder refetch.
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  // Groepeer per dag; binnen een dag chronologisch (backend sorteert al).
  const days = useMemo(() => {
    const map = new Map()
    for (const s of slots) {
      const key = new Date(s.starts_at).toDateString()
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(s)
    }
    return [...map.entries()]
  }, [slots])

  return (
    <div>
      <PageHeader
        icon={CalendarClock}
        title="Programma"
        subtitle="Wat gebeurt er wanneer — op de Zaal, aan de Bar en op de Show"
      />

      <div className="mb-4">
        <SubNav isAdmin={isAdmin} items={[
          { to: '/programma',       label: 'Tijdlijn', icon: CalendarClock },
          { to: '/programma/admin', label: 'Agenda beheren', icon: LucideIcons.Settings, adminOnly: true },
        ]} />
      </div>

      {/* Track-legende — leert de kleurcode aan voor de rest van de pagina */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        {Object.entries(TRACKS).map(([key, t]) => {
          const Icon = t.icon
          return (
            <span key={key} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <Icon className="h-3.5 w-3.5" style={{ color: t.color }} />
              {t.label}
            </span>
          )
        })}
      </div>

      {loading && <p className="text-sm text-muted-foreground">Laden…</p>}
      {error   && <p className="text-sm text-signal">{error}</p>}

      {!loading && !error && slots.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <CalendarClock className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              De agenda is nog leeg.{isAdmin && ' Voeg momenten toe via "Agenda beheren".'}
            </p>
          </CardContent>
        </Card>
      )}

      {days.map(([dayKey, daySlots]) => (
        <section key={dayKey} className="mb-8">
          <h2 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            {fmtDay(daySlots[0].starts_at)}
          </h2>
          <div className="space-y-2">
            {daySlots.map(slot => (
              <SlotRow key={slot.id} slot={slot} nowTs={nowTs} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

/* ── Eén moment in de tijdlijn ───────────────────────────────────────
   De tijd staat links in een vaste kolom zodat alle starttijden onder
   elkaar uitlijnen — je leest de dag als een lijst, niet als losse kaarten. */
function SlotRow({ slot, nowTs }) {
  const track = TRACKS[slot.track] ?? TRACKS.zaal
  const TrackIcon = track.icon
  const SlotIcon = slot.icon_name ? (LucideIcons[slot.icon_name] ?? null) : null

  const start = new Date(slot.starts_at).getTime()
  const end   = slot.ends_at ? new Date(slot.ends_at).getTime() : null
  const isLive = start <= nowTs && (end === null || end >= nowTs)
  const isPast = end !== null ? end < nowTs : start < nowTs - 2 * 3600_000

  const href = slotHref(slot)

  const body = (
    <div className={`flex items-start gap-4 p-3 rounded-[4px] border transition-colors ${
      isLive ? 'border-acid bg-acid/5'
      : isPast ? 'border-border/40 opacity-55'
      : 'border-border bg-card hover:border-acid/40'
    }`}
      style={{ borderLeft: `2px solid ${isLive ? '#B4FF39' : track.color}` }}
    >
      {/* Tijdkolom */}
      <div className="w-[4.5rem] shrink-0 text-right">
        <div className="font-mono text-sm tabular-nums">{fmtTime(slot.starts_at)}</div>
        {slot.ends_at && (
          <div className="font-mono text-[10px] text-muted-foreground tabular-nums">{fmtTime(slot.ends_at)}</div>
        )}
      </div>

      {/* Track-icoon */}
      <div className="pt-0.5 shrink-0">
        {SlotIcon
          ? <SlotIcon className="h-4 w-4" style={{ color: track.color }} />
          : <TrackIcon className="h-4 w-4" style={{ color: track.color }} />}
      </div>

      {/* Inhoud */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-semibold text-sm ${slot.is_highlight ? 'text-acid' : ''}`}>{slot.title}</span>
          {isLive && (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-acid">
              <span className="w-1 h-1 rounded-full bg-acid" />bezig
            </span>
          )}
          {slot.is_highlight && !isLive && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-gold">hoofdmoment</span>
          )}
        </div>
        {slot.description && (
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{slot.description}</p>
        )}
      </div>

      {href && <ArrowRight className="h-4 w-4 text-muted-foreground self-center shrink-0" />}
    </div>
  )

  return href
    ? <Link to={href} className="block no-underline">{body}</Link>
    : body
}

/** Vertaal een slot-koppeling naar een route in de app. */
function slotHref(slot) {
  if (!slot.link_kind) return null
  switch (slot.link_kind) {
    case 'tournament':   return slot.link_ref ? `/tournaments/${slot.link_ref}` : '/tournaments'
    case 'kempenvision': return '/kempenvision'
    case 'imagecompo':   return '/imagecompo'
    case 'url':          return slot.link_ref || null
    default:             return null
  }
}
